import { useHistory, generatePath } from 'react-router-dom';
import { createPath } from 'history';
import { useInvariableMemo } from '../invariable-hooks';
/**
 * Create parameterized history to check passed arguments
 *
 * @example
 * type Keys = 'lorem' | 'ipsum' | 'dolor';
 *
 * // interface for routes that has url params like '/lorem/:year(\\d+)' (optional)
 * interface UrlParams {
 *   lorem: { year: number };
 *   ipsum: { name: 'Search' | 'Display' };
 * }
 *
 * // interface for routes that has query params like '/dolor?to=xxx' (optional)
 * interface QueryParams {
 *   dolor: { to: string };
 * }
 *
 * // map keys to react router routes
 * const UrlMap: Record<Keys, string> = {
 *   dolor: '/',
 *   lorem: '/lorem/:year(\\d+)',
 *   ipsum: '/ipsum/:name(Search|Display)',
 * };
 *
 * export const useCoolHistory = () => {
 *     return useParamsHistory<Keys, UrlParams, QueryParams>(UrlMap);
 * }
 *
 * // component
 * export const MyCoolComponent = () => {
 *     const history = useCoolHistory();
 *     const onClick = history.push('lorem', { year: 2021 });
 *
 *     return <Button onClick={onClick}>cool</Button>
 * }
 */
export const useParamsHistory = (paths, urlHandlers, queryHandlers) => {
    const history = useHistory();
    return useInvariableMemo(() => {
        const generateLocation = (path, urlParams, queryParams) => {
            let searchParams = undefined;
            if (queryParams) {
                const hand = queryHandlers === null || queryHandlers === void 0 ? void 0 : queryHandlers[path];
                if (hand) {
                    searchParams = '?' + hand.toString(queryParams);
                }
                else {
                    searchParams = '?' + new URLSearchParams(queryParams).toString();
                }
            }
            const urlHandler = urlHandlers === null || urlHandlers === void 0 ? void 0 : urlHandlers[path];
            return {
                pathname: generatePath(paths[path], urlHandler ? urlHandler.toString(urlParams) : urlParams),
                search: searchParams,
            };
        };
        return {
            history,
            path: ((key, urlParams, queryParams) => {
                return createPath(generateLocation(key, urlParams, queryParams));
            }),
            href: ((key, urlParams, queryParams) => {
                return history.createHref({
                    pathname: createPath(generateLocation(key, urlParams, queryParams)),
                });
            }),
            push: ((key, urlParams, queryParams) => {
                history.push(createPath(generateLocation(key, urlParams, queryParams)));
            }),
            replace: ((key, urlParams, queryParams) => {
                history.replace(createPath(generateLocation(key, urlParams, queryParams)));
            }),
        };
    });
};
//# sourceMappingURL=history.js.map